import { Button, Checkbox, Fade, Stack, styled, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import Loader from './Loader';
import { ClassStatus } from '../../../context/types';
import InviteStudentsContainer from '../InviteStudentsContainer';

const Root = styled(Stack)`
    margin-top: 16px;
    margin-bottom: 16px;
    flex-direction: row;

    ${({ theme }) => theme.breakpoints.down('sm')} {
        flex-direction: column;
        gap: 16px;
    }
` as typeof Stack;

const ContainerSection = styled(Stack)`
    flex-direction: row;
    align-items: center;
` as typeof Stack;

const ButtonSection = styled(Stack)`
    flex-direction: row;
    gap: 10px;
` as typeof Stack;

export type SendInvitesProps = {
    onCancel?: () => void;
};

const SendInvites = (props: SendInvitesProps) => {
    const { onCancel } = props;

    const { t } = useTranslation('invite_students');
    const {
        classInfo,
        canSubmit,
        hasValidationErrors,
        returnToRosterUrl,
        inviteStudentsStatus,
        sendInvitations,
        sendEmails,
        setSendEmails,
        includeCalendarInvite,
        setIncludeCalendarInvite,
        settings
    } = useContainer(InviteStudentsContainer);

    const handleEmailCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSendEmails(event.target.checked);
    };

    const handleCalendarCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIncludeCalendarInvite(event.target.checked);
    };

    const handleCancelClick = () => {
        if (onCancel) {
            onCancel();
            return;
        }

        window.location.replace(returnToRosterUrl);
    };

    const displayEmailOptions = classInfo && classInfo?.status !== ClassStatus.Draft && settings.send_invite_emails;

    return (
        <Root>
            <Fade in={displayEmailOptions}>
                <ContainerSection>
                    <Typography>{t('send-email-on-invite')}</Typography>
                    <Checkbox checked={sendEmails} onChange={handleEmailCheckboxChange} />

                    <Typography>{t('include-calendar-invite')}</Typography>
                    <Checkbox checked={includeCalendarInvite} onChange={handleCalendarCheckboxChange} />
                </ContainerSection>
            </Fade>

            <ContainerSection ml="auto">
                {inviteStudentsStatus === 'LOADING' ? (
                    <Loader />
                ) : (
                    <ButtonSection>
                        <Button color="secondary" variant="contained" onClick={handleCancelClick}>
                            {t('components.cancel')}
                        </Button>

                        <Tooltip
                            title={hasValidationErrors ? t('components.invite-error-tooltip') : ''}
                            placement="left"
                            arrow
                        >
                            {/* Tooltip does not work with disabled button. So it needs to be wrapped in span. */}
                            <span>
                                <Button
                                    disabled={!canSubmit}
                                    color="primary"
                                    variant="contained"
                                    onClick={sendInvitations}
                                >
                                    {t('components.send-invites')}
                                </Button>
                            </span>
                        </Tooltip>
                    </ButtonSection>
                )}
            </ContainerSection>
        </Root>
    );
};

export default SendInvites;
